import clsx from "clsx";
import { useState } from "react";

import EmojiSelector from "common/components/EmojiSelector";
import RenderIf from "common/components/RenderIf";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import { useActiveConversationContext } from "../../../context/ActiveConversationContext";
import useConversationMessagesUpdates from "../../../datahooks/useConversationMessagesUpdates";

import type { ConversationMessage } from "../../../types";

import SmileyAdd from "assets/icons/smiley-add.svg?react";

interface ReactionsProps {
  accountId: string;
  message: ConversationMessage;
}

const MAX_REACTIONS = 3;

export default function Reactions({ accountId, message }: ReactionsProps) {
  const isDesktop = useTwBreakpoint("lg");
  const { activeConversation } = useActiveConversationContext();
  const { sendMessageReaction } = useConversationMessagesUpdates({
    conversationUrn: activeConversation.conversation_urn,
  });

  const [isExpanded, setIsExpanded] = useState(false);

  const { reactions, sender, urn } = message;

  const isMine = accountId === sender.id;

  function onMessageReact(emoji: string) {
    // whether the user reacted or not
    const { reader_reacted: isReacted } =
      reactions.find((reaction) => reaction.emoji === emoji) || {};

    sendMessageReaction({
      accountId,
      emoji,
      messageUrn: urn,
      unreact: isReacted,
    });
  }

  // if desktop or expanded, show all reactions
  const visibleReactions =
    isDesktop || isExpanded ? reactions : reactions.slice(0, MAX_REACTIONS);

  const hiddenCount =
    reactions.length && reactions.length > MAX_REACTIONS
      ? reactions.length - MAX_REACTIONS
      : 0;

  return (
    <div
      className={clsx([
        "flex max-w-[23.25rem] flex-row flex-wrap items-center gap-1",
        isMine ? "justify-end" : "justify-start",
      ])}
    >
      {visibleReactions?.map(({ emoji, count, reader_reacted }) => (
        <button
          key={emoji}
          type="button"
          className={clsx(
            "flex flex-row items-center justify-center gap-1.5 h-7 md:h-6 text-sm lg:text-xs rounded-full px-2 lg:px-1.5 transition-[border,background] hover:bg-black-100 hover:border-black-200 border",
            reader_reacted
              ? "bg-purple-100 border-purple-100"
              : "bg-black-100 border-black-100",
          )}
          onClick={() =>
            sendMessageReaction({
              accountId,
              emoji,
              messageUrn: urn,
              unreact: reader_reacted,
            })
          }
        >
          <span>{emoji}</span>
          <span>{count}</span>
        </button>
      ))}

      {/* More reactions button (mobile only) */}
      <RenderIf condition={!isDesktop && hiddenCount > 0}>
        <button
          type="button"
          className="flex h-fit flex-row items-center justify-center gap-2 rounded-full bg-black-100 px-2 py-1 text-xs"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? "Show less" : `+ ${hiddenCount} more`}
        </button>
      </RenderIf>

      {/* emoji selector */}
      <RenderIf condition={reactions.length > 0}>
        <EmojiSelector
          buttonProps={{
            children: <SmileyAdd />,
            size: "xs",
            variant: "outline-black",
          }}
          onEmojiSelection={onMessageReact}
        />
      </RenderIf>
    </div>
  );
}
